import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import {
  Accordion,
  Avatar,
  Badge,
  Button,
  Checkbox,
  SpinnerLoader,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import {
  ChevronDown,
  DoorOpen,
  LayersThree02,
  Users01,
} from '@kula-ai/icons-core';
import { Box, Flex } from '@chakra-ui/react';
import { colors } from '@/theme/foundations/colors';
import { memberCountLabel, onJobTagSummary } from './useRecipientSections';
import {
  costOfAdding,
  EMPTY_COST_MODEL,
  recipientLimitHint,
  RECIPIENT_OVER_LIMIT_HINT,
  tallyRecipients,
  type RecipientCostModel,
} from './recipientLimits';
import type { RecipientOption, RecipientSection } from './useRecipientSections';

interface SectionChrome {
  Icon: ComponentType<{ size?: number; color?: string }>;
}

const SECTION_CHROME: Record<RecipientSection['id'], SectionChrome> = {
  job_roles: { Icon: LayersThree02 },
  kula_roles: { Icon: DoorOpen },
  users: { Icon: Users01 },
};

const SECTION_ORDER: RecipientSection['id'][] = [
  'job_roles',
  'kula_roles',
  'users',
];

/**
 * The list when there is room for everything, and the Cancel/Add strip below it.
 * The footer never scrolls: a panel whose Add button is past the fold cannot be
 * committed at all.
 */
const LIST_MAX_HEIGHT = 360;
const FOOTER_HEIGHT = 57;

/** `maxMenuHeight`, so placement is chosen against this panel, not the 300 default. */
export const RECIPIENT_MENU_MAX_HEIGHT = LIST_MAX_HEIGHT + FOOTER_HEIGHT;

/** Breathing room between the panel and the edge of the window. */
const VIEWPORT_GUTTER = 12;

/** About three rows. Below this the panel is not worth showing downwards. */
const LIST_MIN_HEIGHT = 160;

/** kula's `menu` style sets `marginTop: 3px`; the flip has to clear it twice. */
const TRIGGER_GAP = 3;

/** A kula Select control, for the one render before the real one is measurable. */
const FALLBACK_CONTROL_HEIGHT = 38;

/**
 * The field this panel belongs to.
 *
 * The panel is portalled, so it is no relation of its field in the DOM. It does
 * hold one thing that is: react-select keeps focus in its own input for as long
 * as the menu is open, which this panel goes out of its way to preserve
 * (see the `onMouseDown` below). Returns undefined rather than guessing when
 * that does not hold — the caller falls back to a nominal height.
 */
const triggerRectOf = (panel: HTMLElement | null): DOMRect | undefined => {
  const focused = panel?.ownerDocument?.activeElement;
  if (!(focused instanceof HTMLElement)) return undefined;
  const control = focused.closest('[class*="control"]');
  return control instanceof HTMLElement
    ? control.getBoundingClientRect()
    : undefined;
};

/**
 * `minMenuHeight`: where react-select stops shrinking and flips above the field.
 * Must be this panel's own floor — at its default 140 there is a band where it
 * keeps the panel below with less room than the panel shrinks to, putting the
 * footer back under the fold.
 */
export const RECIPIENT_MENU_MIN_HEIGHT = LIST_MIN_HEIGHT + FOOTER_HEIGHT;

export interface RecipientPickerConfig {
  sections: RecipientSection[];
  /** Every option the picker has rendered, so a chip keeps its label. */
  optionByValue: Map<string, { value: string; label: string; image?: string }>;
  /** The scroll container has reached the end of the list — page in the rest. */
  onEndReached?: () => void;
  /**
   * What each row weighs against the cap. Absent where none is enforced: the
   * interview-plan composer often has no job, so a count would be a guess.
   */
  costModel?: RecipientCostModel;
  /** The cap for this field. Absent means no cap. */
  limit?: number;
  /**
   * What the sibling field holds. Cc and Bcc are two react-selects spending one
   * budget and neither can see the other, so the composer tells them.
   */
  otherFieldValues?: string[];
}

/**
 * Typed structurally on purpose: kula's `Select` runs react-select 5 and
 * core-web's own dependency is 4, so an imported `MenuProps` would type against
 * v4 and compile clean while misbehaving at runtime.
 */
interface RecipientMenuProps {
  innerProps?: Record<string, unknown>;
  /**
   * Which side react-select placed the panel on. Its menu CSS reads this; ours is
   * Chakra, so the flip must be honoured here or `auto` moves only the anchor.
   */
  placement?: 'bottom' | 'top' | 'auto';

  /** react-select's processed options — kula-wrapped, so they carry `__original`. */
  options?: ReadonlyArray<{ value?: unknown }>;
  getValue: () => ReadonlyArray<{ value?: unknown }>;
  setValue: (_value: unknown, _action: string) => void;
  selectProps: {
    inputValue?: string;
    onMenuClose?: () => void;
    recipientPicker?: RecipientPickerConfig;
  };
}

const HOVER_DELAY_MS = 150;

/**
 * `open` is owned here, not by Radix. Radix opens on `pointermove` behind a latch
 * it lowers only on `pointerleave`, and closing never lowers it — so ticking a
 * row (the badge sits inside the checkbox) left the hover dead until the pointer
 * travelled off the badge and back. Owning `open` sidesteps the latch; cancelling
 * the close paths instead leaves the tooltip stuck open, since dismissal is also
 * how Radix puts it away.
 */
const RolesTooltip = ({
  all,
  children,
}: {
  all: string;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const openTimer = useRef<number | undefined>(undefined);

  const hide = () => {
    window.clearTimeout(openTimer.current);
    openTimer.current = undefined;
    setOpen(false);
  };

  const show = () => {
    // `pointermove` fires continuously; without this every pixel restarts the
    // wait and the tooltip never arrives while the pointer drifts.
    if (open || openTimer.current) return;
    openTimer.current = window.setTimeout(() => {
      openTimer.current = undefined;
      setOpen(true);
    }, HOVER_DELAY_MS);
  };

  useEffect(() => () => window.clearTimeout(openTimer.current), []);

  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={(next) => (next ? show() : hide())}>
        {/* A Box, not the child: `asChild` hands over a ref only a forwardRef
            element can take. `aria-label` carries the list to non-hoverers. */}
        <TooltipTrigger asChild>
          <Box
            as="span"
            display="inline-flex"
            minW={0}
            aria-label={all}
            onPointerMove={show}
            onPointerLeave={hide}
          >
            {children}
          </Box>
        </TooltipTrigger>
        <TooltipContent side="top" collisionPadding={12}>
          {all}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const RolesHover = ({
  tags,
  children,
}: {
  tags?: string[];
  children: ReactNode;
}) => {
  // Nothing is hidden behind a single role, so nothing to open.
  if ((tags ?? []).length < 2) return children;

  return <RolesTooltip all={(tags ?? []).join(', ')}>{children}</RolesTooltip>;
};

/**
 * The roles a user holds on this job. One reads plainly; more names the first and
 * counts the rest, with all of them in the hover — the row carries a name and an
 * address too. Only the badge says "on job", because only the badge stands alone.
 */
const OnJobTags = ({ tags }: { tags?: string[] }) => {
  const summary = onJobTagSummary(tags);
  if (!summary) return null;

  return (
    <RolesHover tags={tags}>
      <Badge color="grey" data-test-id="recipient-option-tag">
        {summary}
      </Badge>
    </RolesHover>
  );
};

/**
 * Role rows are 44px with an 18px initials chip; user rows are 52px with a 24px
 * avatar and a second line. Both share the 12px gutter and 6px column gaps.
 */
const SectionRow = ({
  option,
  checked,
  onToggle,
}: {
  option: RecipientOption;
  checked: boolean;
  onToggle: () => void;
}) => {
  const isRole = Boolean(option.initials);
  const isDisabled = Boolean(option.disabled);
  // A person's row has a line under the name; a role token does not.
  const hasSecondLine = Boolean(option.roleLine || option.email);

  return (
    <Flex
      as="button"
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-disabled={isDisabled || undefined}
      tabIndex={isDisabled ? -1 : undefined}
      // The mark and avatar belong to the name, not to the pair of lines —
      // centred, they float between the two. One line has nothing to hang from.
      align={hasSecondLine ? 'flex-start' : 'center'}
      gap="6px"
      px="12px"
      pt={hasSecondLine ? TWO_LINE_ROW_PADDING_TOP : undefined}
      h={isRole ? '44px' : '52px'}
      w="100%"
      textAlign="left"
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      opacity={isDisabled ? 0.5 : 1}
      borderBottom="1px solid"
      borderColor="secondary-100"
      _hover={isDisabled ? undefined : { bg: 'secondary-25' }}
      onMouseDown={(event) => event.preventDefault()}
      onClick={isDisabled ? undefined : onToggle}
      data-test-id={`recipient-option-${option.value}`}
    >
      {/* The row owns the click; the box is the indicator, so a tick and a
          row click cannot both fire and cancel each other out. */}
      <Box pointerEvents="none" display="flex">
        <Checkbox size="sm" checked={checked} readOnly />
      </Box>

      {isRole ? (
        <Flex
          align="center"
          justify="center"
          minW="18px"
          h="18px"
          px="4px"
          flexShrink={0}
          borderRadius="4px"
          // `pastel-3-light`, not design-tokens' `pastel-3light`: Chakra cannot
          // resolve that spelling and silently emits no background.
          bg="pastel-3-light"
          data-test-id="recipient-option-initials"
        >
          {/* kula Text bottoms out at 12px; these initials are 10px. */}
          <Box as="span" fontSize="10px" fontWeight={650} color="base-black">
            {option.initials}
          </Box>
        </Flex>
      ) : (
        <Avatar size={24} src={option.image} name={option.label} />
      )}

      <Box flex="1" minW={0}>
        <Flex align="center" gap="6px" minW={0}>
          {/* The box, not the Text: `truncate` needs somewhere to shrink to,
              and a flex item's floor is its own content. */}
          <Box minW={0}>
            <Text variant={isRole ? 'p' : 'h4'} truncate>
              {option.label}
            </Text>
          </Box>
        </Flex>
        {/* The roles that put this person on the job, where a user row shows an
            address. All of them named — the line has the row to itself. */}
        {option.roleLine ? (
          // kula's `Text` opens its own tooltip only once the label overflows,
          // which is the contract here: every role is already named on screen.
          <Text variant="p" truncate data-test-id="recipient-option-roles">
            {option.roleLine}
          </Text>
        ) : (
          option.email && (
            <Text variant="p" truncate>
              {option.email}
            </Text>
          )
        )}
      </Box>
      <Flex align="center" gap="6px" flexShrink={0}>
        {option.hint ? (
          <Badge color="grey" data-test-id="recipient-option-hint">
            {option.hint}
          </Badge>
        ) : option.memberCount !== undefined ? (
          // Roles only — a person is one person, and the row already says who.
          <Badge color="grey" data-test-id="recipient-option-count">
            {memberCountLabel(option.memberCount)}
          </Badge>
        ) : (
          // Not on a row that already counts its roles underneath — the badge
          // would be the same fact twice over, on the same row.
          !option.roleLine && <OnJobTags tags={option.tags} />
        )}
      </Flex>
    </Flex>
  );
};

/** Also how `closeMenuOnScroll` tells the menu's own scrolling from the page's. */
export const MENU_TEST_ID = 'recipient-picker-menu';

/** Where centring already started a two-line row's content: (52px − ~38px) / 2. */
const TWO_LINE_ROW_PADDING_TOP = '7px';

const SEARCHING_LABEL = 'Searching…';
const LOADING_LABEL = 'Loading…';

/**
 * Replaces the rows rather than covering them. Sitting on top left the previous
 * search's names pickable underneath, and Add commits without a second look.
 * Three rows tall, so the section neither collapses nor jumps when they return.
 */
const LoadingSection = ({ searching }: { searching: boolean }) => (
  <Flex
    align="center"
    justify="center"
    gap="8px"
    minH="132px"
    borderBottom="1px solid"
    borderColor="secondary-100"
    // The rows are gone, not covered — say so rather than leave a screen reader
    // waiting on a list that is not coming back yet.
    aria-busy="true"
    // kula's spinner announces itself (`role="status"`); this does not repeat it.
    data-test-id="recipient-section-loading"
  >
    <SpinnerLoader size="sm" />
    <Text variant="p" color="muted">
      {searching ? SEARCHING_LABEL : LOADING_LABEL}
    </Text>
  </Flex>
);

/**
 * A later page, under the rows it will join. Paging keeps its rows: they are
 * still the right answer, and blanking the list under a scroller is worse.
 */
const LoadingMoreRow = () => (
  <Flex
    align="center"
    gap="6px"
    px="12px"
    h="44px"
    borderBottom="1px solid"
    borderColor="secondary-100"
    data-test-id="recipient-section-loading-more"
  >
    <SpinnerLoader size="sm" />
    <Text variant="p" color="muted">
      {LOADING_LABEL}
    </Text>
  </Flex>
);

/**
 * The To/Cc/Bcc dropdown: job roles, Kula roles and users in three accordions.
 *
 * Rendered as react-select's `Menu`, so kula's chips, placeholder, errors and
 * card chrome all stay — only the panel is ours. Selection is staged locally and
 * committed on Add, which react-select has no notion of; `setValue` bridges it.
 * Search is react-select's own input: roles filter in memory, users re-query.
 */
export const RecipientPickerMenu = (props: RecipientMenuProps): ReactNode => {
  const { selectProps, getValue, setValue, innerProps, options, placement } =
    props;
  const { recipientPicker, onMenuClose, inputValue } = selectProps;
  const sections = recipientPicker?.sections ?? [];
  const searching = Boolean(inputValue?.trim());
  const onEndReached = recipientPicker?.onEndReached;

  /**
   * Not memoised on `getValue`: react-select reuses that function identity across
   * value changes, so a memo freezes this at whatever the field held on first
   * render — nothing at all, if the value had not hydrated yet.
   */
  const committed = (getValue() ?? []).map((option) =>
    String(option?.value ?? '')
  );

  /**
   * Deltas, not a copy of the selection. Copying the field into state meant a
   * panel opened before the recipients landed showed nothing ticked and dropped
   * them all on Add (CORE-5384); a snapshot at first tick only narrowed the
   * window. Reading the field as it is now, minus removals plus additions,
   * carries a late arrival whenever it lands.
   */
  const [added, setAdded] = useState<string[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);

  const staged = [
    ...committed.filter((value) => !removed.includes(value)),
    // Only what the field does not already hold, so a value the user ticked
    // and that then arrived on its own is not listed twice.
    ...added.filter((value) => !committed.includes(value)),
  ];

  /**
   * Unmemoised for the same reason as `committed`: a stale budget would grey out
   * free rows or offer full ones. Tallied with the sibling field, so a person on
   * both Cc and Bcc counts once — as the backend counts them.
   */
  const costModel = recipientPicker?.costModel ?? EMPTY_COST_MODEL;
  const tally = tallyRecipients(
    [...staged, ...(recipientPicker?.otherFieldValues ?? [])],
    costModel
  );
  const remaining =
    recipientPicker?.limit === undefined
      ? Infinity
      : recipientPicker.limit - tally.total;

  const toggle = (value: string) => {
    const ticked = staged.includes(value);
    setAdded((current) =>
      ticked
        ? current.filter((item) => item !== value)
        : current.includes(value)
          ? current
          : [...current, value]
    );
    setRemoved((current) =>
      ticked
        ? current.includes(value)
          ? current
          : [...current, value]
        : current.filter((item) => item !== value)
    );
  };

  // Nothing to apply. Still enabled once they differ, so unticking everything
  // and pressing Add clears the row rather than leaving removal to the chips.
  const unchanged =
    staged.length === committed.length &&
    staged.every((value) => committed.includes(value));

  // Rows are a fixed 44/52px, so a pixel threshold is a row threshold: page in
  // about two rows early, so the list grows before the scroll stops.
  const handleScroll = (event: { currentTarget: HTMLDivElement }) => {
    if (!onEndReached) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= 96) onEndReached();
  };

  const commit = () => {
    const byValue = recipientPicker?.optionByValue;
    // kula's `useSelectValue` reads `.value` off every option's `__original`, so
    // a plain `{value,label}` throws and Add silently does nothing. Prefer
    // react-select's processed option; otherwise wrap it the same way.
    const wrapped = new Map(
      (options ?? []).map((option) => [String(option?.value ?? ''), option])
    );
    setValue(
      staged.map((value) => {
        const known = wrapped.get(value);
        if (known) return known;
        const original = byValue?.get(value) ?? { value, label: value };
        return { ...original, __original: original };
      }),
      'select-option'
    );
    onMenuClose?.();
  };

  /**
   * Fit the panel to the room the window has. react-select works this out but
   * hands it to `MenuList`, and this panel renders its own list. Same sum, same
   * viewport — `menuPosition: 'fixed'` means the rect is already in viewport
   * coordinates.
   *
   * Measured off the edge the panel hangs from, never its height: the anchor is
   * the field either way, so shrinking the list cannot move the next reading —
   * which a height-derived one would, once round the loop.
   */
  const panelRef = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState<number | null>(null);
  /**
   * How far the panel is lifted to sit above its field, in px. 0 when it hangs
   * below, which is the usual case.
   *
   * The panel has to decide this itself. kula places its own menus with
   * floating-ui inside `FloatingMenuShell` — flip included — and every menu it
   * ships renders through that shell. This picker replaces `components.Menu`
   * outright, so none of it runs: `placement` arrives as `bottom` however little
   * room there is, and react-select's own `menuPlacement: 'auto'` (with the
   * `minMenuHeight` this panel passes for exactly this) never gets to decide
   * either. The panel then shrank to its floor and hung off the bottom of the
   * window, taking Add and Cancel with it (CORE-5441).
   *
   * kula exports neither the shell nor its trigger context, so there is nothing
   * to reuse — see the note in the PR: being able to supply menu CONTENT while
   * keeping kula's shell is the thing missing upstream.
   */
  const [flipped, setFlipped] = useState<{ clearance: number } | null>(null);

  /**
   * Where the panel hangs from, when the field itself cannot be measured.
   *
   * Read once and kept. The panel's own rect moves the moment it is flipped, so
   * re-reading it would hand the next decision the result of the last one and
   * the two would trade places forever.
   */
  const fallbackAnchor = useRef<number | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const panel = panelRef.current;
      const rect = panel?.getBoundingClientRect();
      if (!rect) return;

      if (placement === 'top') {
        setFlipped(null);
        setRoom(rect.bottom - VIEWPORT_GUTTER);
        return;
      }

      // The FIELD, not the panel. Its rect is unaffected by where the panel ends
      // up, so the reading cannot chase its own answer — which is the whole
      // reason floating-ui measures the reference element rather than the
      // floating one.
      const trigger = triggerRectOf(panel);
      const clearance =
        (trigger?.height ?? FALLBACK_CONTROL_HEIGHT) + TRIGGER_GAP * 2;
      const anchorTop = trigger
        ? trigger.bottom + TRIGGER_GAP
        : (fallbackAnchor.current ??= rect.top);

      const below = window.innerHeight - anchorTop - VIEWPORT_GUTTER;
      const above = anchorTop - clearance - VIEWPORT_GUTTER;

      // Only when the panel cannot even reach its floor below, and turning it
      // over genuinely buys room.
      const flip = below < RECIPIENT_MENU_MIN_HEIGHT && above > below;

      setFlipped(flip ? { clearance } : null);
      setRoom(flip ? above : below);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [placement]);

  // The footer is not negotiable, so the list gives up the difference, down to
  // a floor of about three rows.
  const listMaxHeight =
    room === null
      ? LIST_MAX_HEIGHT
      : Math.min(
          LIST_MAX_HEIGHT,
          Math.max(LIST_MIN_HEIGHT, room - FOOTER_HEIGHT)
        );

  const ordered = SECTION_ORDER.map((id) =>
    sections.find((section) => section.id === id)
  ).filter((section): section is RecipientSection => Boolean(section));

  return (
    <Box
      ref={panelRef}
      {...innerProps}
      // Keeps focus in react-select's input for every press inside the menu:
      // a press on chrome rather than an enabled row — a section header, plain
      // padding, a disabled option — blurs it, and react-select closes on blur.
      // `onClick` still fires, so rows and the footer buttons are unaffected.
      onMouseDown={(event) => event.preventDefault()}
      // On a flip react-select anchors at the field's top edge and leaves the
      // lift to its own emotion CSS, which this Chakra panel never gets. Without
      // this the flip moves the anchor only, and the panel covers its field.
      // `-100%` is the panel's own height, resolved by CSS — so the lift never
      // has to be measured, and cannot feed the measurement above.
      transform={
        placement === 'top'
          ? 'translateY(-100%)'
          : flipped
            ? `translateY(calc(-100% - ${flipped.clearance}px))`
            : undefined
      }
      bg="white"
      borderRadius="6px"
      border="1px solid"
      borderColor="secondary-100"
      boxShadow="drop-shadows-bottom"
      overflow="hidden"
      data-test-id={MENU_TEST_ID}
    >
      <Box maxH={`${listMaxHeight}px`} overflowY="auto" onScroll={handleScroll}>
        <Accordion.Root
          type="multiple"
          defaultValue={SECTION_ORDER}
          indicatorPosition="left"
          // A custom indicator opts out of kula's built-in chevron rotation, so
          // the open-state flip is re-declared here against our own class.
          indicator={
            <Flex
              align="center"
              justify="center"
              w="52px"
              // Stretch, not a fixed 52px: the trigger's height includes its
              // bottom rule, so a square cell paints 1px over it — which is how
              // that rule came to be missing under the chevron.
              alignSelf="stretch"
              flexShrink={0}
              borderRight="1px solid"
              borderColor="secondary-100"
            >
              <ChevronDown
                className="recipient-chevron transition-transform duration-200"
                size={18}
                color={colors['secondary-700']}
              />
            </Flex>
          }
          // A rule across the full width, chevron cell included: without it the
          // only thing parting a heading from its first row is grey to white.
          triggerClassName="h-[52px] gap-0 p-0 border-b border-secondary-100 bg-secondary-50 hover:bg-secondary-50 [&[data-state=open]_.recipient-chevron]:rotate-180"
          // Rows and headers draw their own separators, so the item wrapper's
          // would double up at every section boundary.
          itemClassName="border-b-0"
          contentClassName="[&>div]:pb-0"
          // Radix would otherwise steal the keystrokes from react-select's input.
          onKeyDown={(event) => event.stopPropagation()}
        >
          {ordered.map((section) => {
            const { Icon } = SECTION_CHROME[section.id];

            return (
              <Accordion.Item key={section.id} value={section.id}>
                <Accordion.Trigger
                  data-test-id={`recipient-section-${section.id}`}
                >
                  <Flex
                    align="center"
                    gap="6px"
                    pl="12px"
                    pr="12px"
                    w="100%"
                    borderBottom="1px solid"
                    borderColor="secondary-100"
                    h="52px"
                  >
                    <Icon size={18} color={colors['secondary-700']} />
                    <Flex align="center" gap="8px">
                      <Text variant="overhead" color="heading">
                        {section.title}
                      </Text>
                      {section.count !== undefined && (
                        <Badge type="outline" color="white">
                          {section.count}
                        </Badge>
                      )}
                    </Flex>
                  </Flex>
                </Accordion.Trigger>
                <Accordion.Content>
                  {/* In place of the rows, not over them: while this is up there
                      is nothing here to pick, which is the point of it. */}
                  {section.isReloading ? (
                    <LoadingSection searching={searching} />
                  ) : section.options.length === 0 ? (
                    <Text variant="p" style={{ padding: '12px' }}>
                      No matches
                    </Text>
                  ) : (
                    section.options.map((option) => {
                      // From the ORIGINAL option, before the override below:
                      // it reads `!option.disabled && …`, so a row we just
                      // disabled would render an already-picked recipient
                      // unticked, and Add would drop them without a word.
                      const checked =
                        !option.disabled && staged.includes(option.value);
                      const adds =
                        checked || option.disabled
                          ? 0
                          : costOfAdding(option.value, tally, costModel);
                      // Per row, not one flag: "no room left" and "this role
                      // will not fit in what is left" are different rows, and
                      // the cost tells them apart for free. `adds > 0` first,
                      // because a prefilled selection can start over the cap and
                      // `remaining` goes negative — without it every free row,
                      // ticked ones included, greys out and the sender can
                      // neither send nor cut it back.
                      const blocked = adds > 0 && adds > remaining;

                      return (
                        <SectionRow
                          key={option.value}
                          option={
                            blocked
                              ? {
                                  ...option,
                                  disabled: true,
                                  hint:
                                    remaining <= 0
                                      ? recipientLimitHint(
                                          recipientPicker?.limit
                                        )
                                      : RECIPIENT_OVER_LIMIT_HINT,
                                }
                              : option
                          }
                          checked={checked}
                          onToggle={() => toggle(option.value)}
                        />
                      );
                    })
                  )}
                  {/* Under the rows, not over them — the listed ones are not
                      going anywhere. Also the scroll handler's sentinel, so it
                      has to be in the flow. */}
                  {!section.isReloading &&
                    (section.isLoading || section.hasMore) && (
                      <LoadingMoreRow />
                    )}
                </Accordion.Content>
              </Accordion.Item>
            );
          })}
        </Accordion.Root>
      </Box>

      <Flex
        justify="flex-end"
        gap="6px"
        p="12px"
        borderTop="1px solid"
        borderColor="secondary-100"
      >
        <Button
          variant="secondary"
          size="regular"
          onClick={() => onMenuClose?.()}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="regular"
          onClick={commit}
          disabled={unchanged}
          data-test-id="recipient-picker-add"
        >
          {/* Bare, deliberately. It carried the staged count (CORE-5384), but
              that number was the whole selection — so ticking one more person
              on a field of four read "Add (5)". */}
          Add
        </Button>
      </Flex>
    </Box>
  );
};
